
import Vue from 'vue'
import { createNamespacedHelpers } from 'vuex'
import router from '@/router'

import { RButton } from '@/components'
import FileBrowser from '@/components/browser/FileBrowser.vue'
import {
  FileBrowserFile,
  FileBrowserDirectory,
  FileBrowserFileOrDirectory,
  FileBrowserItems,
  UppyUploadResult,
} from '@/components/browser/types'
import FileBrowserToolbox from '@/components/browser/FileBrowserToolbox.vue'
import FileBrowserBreadcrumbs from '@/components/browser/FileBrowserBreadcrumbs.vue'

import { sortAlphabeticallyByAProperty } from '@/services/sorting'

const {
  mapGetters: mapSharedFileGetters,
  mapActions: mapSharedFileActions,
  mapMutations: mapSharedFileMutations,
} = createNamespacedHelpers('sharedFile')

type FileBrowserData = {
  uploadInProgress: boolean
}

type SelectionOptionsObject = {
  download: boolean
}

export default Vue.extend({
  name: 'BrowserView',
  components: {
    RButton,
    FileBrowser,
    FileBrowserBreadcrumbs,
    FileBrowserToolbox,
  },

  data(): FileBrowserData {
    return {
      uploadInProgress: false,
    }
  },

  created() {
    this.loadDirectory().then(() => this.selectAllFiles())
  },

  methods: {
    ...mapSharedFileActions({
      loadDirectory: 'loadSharedDirectory',
      clearSelection: 'clearSelection',
      goToDirectory: 'goToDirectory',
      download: 'downloadMultiple',

      notifyError: 'notification/error',
      notifySuccess: 'notification/success',
    }),
    ...mapSharedFileMutations({
      selectPath: 'selectPath',
      selectMultiple: 'selectMultiplePaths',
      deselectPath: 'deselectPath',
    }),

    selectAllFiles(): void {
      if (!this.items.length) return
      const allFileNames = this.items
        .filter((item: FileBrowserFileOrDirectory) => item.type == 'file')
        .map((item: FileBrowserFileOrDirectory) => item.name)

      if (!allFileNames) return
      this.selectMultiple(allFileNames)
    },

    selectAll(): void {
      this.selectMultiple(this.items.map((item: FileBrowserFileOrDirectory) => item.name))
    },

    handleSelectAll(): void {
      if (this.allSelected) {
        this.clearSelection()
      } else {
        this.selectAll()
      }
    },

    // UPLOAD hooks:
    handleUploadStart(): void {
      this.uploadInProgress = true
    },
    handleUploadError(error: Error): void {
      this.notifyError(error.message)
    },
    async handleUploadComplete(result: UppyUploadResult): Promise<void> {
      await this.loadDirectory()
      this.uploadInProgress = false
      const amountOfFiles = result.successful.length
      this.notifySuccess(
        amountOfFiles > 1
          ? `${amountOfFiles} files were successfully uploaded.`
          : `${result.successful[0].name} was successfully uploaded.`,
      )
    },
    goToFullPathDirectory(path: string): void {
      router.currentRoute.query.directory
        ? this.goToDirectory(router.currentRoute.query.directory + '/' + path)
        : this.goToDirectory(path)
    },
  },

  computed: {
    ...mapSharedFileGetters({
      loadFilesRequest: 'loadFilesRequest',
      files: 'files',
      directories: 'directories',
      isLoadingFiles: 'loadFilesRequestPending',
      shareLinkRequest: 'shareLinkRequest',
      path: 'path',
      hash: 'hash',
      selectedPaths: 'selectedPaths',
      selectedItems: 'selectedItems',
      singleSelectedItem: 'singleSelectedItem', // rename: current name
      permissions: 'permissions',
      linkTitle: 'linkTitle',
    }),
    items(): FileBrowserItems {
      const items: FileBrowserItems = []
      const directoriesArray: FileBrowserDirectory[] = []
      const filesArray: FileBrowserFile[] = []
      this.directories.map((d: any) => {
        directoriesArray.push({
          ...d,
          type: 'dir',
          selected: this.selectedPaths.has(d.name),
        } as FileBrowserDirectory)
      })
      this.files.map((f: any) => {
        filesArray.push({
          ...f,
          type: 'file',
          selected: this.selectedPaths.has(f.name),
        } as FileBrowserFile)
      })
      sortAlphabeticallyByAProperty(directoriesArray)
      sortAlphabeticallyByAProperty(filesArray)
      items.push(...directoriesArray, ...filesArray)
      return items
    },

    canDownload(): boolean {
      // 1. must be at least one selected
      // 2. all selected must be files.
      const found =
        this.selectedItems.length &&
        this.selectedItems.every((item: FileBrowserFileOrDirectory) => {
          return item.type == 'file'
        })
      return !!found
    },
    canUpload(): boolean {
      return this.permissions.upload
    },
    canList(): boolean {
      return this.permissions.list
    },
    selectionOptions(): SelectionOptionsObject {
      return {
        download: this.canDownload,
      }
    },
    directoryNames(): string[] {
      return this.directories.map((d: any) => d.name)
    },
    itemNames(): string[] {
      return this.files.map((f: any) => f.name)
    },
    allSelected(): boolean {
      return this.selectedPaths.size === this.items.length
    },
    mainPrefix(): string {
      return `/shared-link/${this.hash}`
    },
  },
})
